import type { ComponentSchema } from '../../../../../schema-types'
import { SECTION_STYLE_FIELDS, ICON_OPTIONS } from './_fields'

/**
 * Fresh Basket hero: a tint panel with the headline slide (eyebrow, headline, text, two buttons, trust chips,
 * picture) and optional product or custom slides after it, beside up to two product cards. Slide keys match the
 * shared hero (`title`, `subtitle`, `image`, `ctaText`, `ctaLink`, `badge`), so slides from other themes keep showing.
 */
export const schema: ComponentSchema = {
  type: 'hero',
  label: 'Hero',
  description: 'Headline panel with buttons, trust chips and product cards; extra slides rotate after it.',
  supportedPages: ['home', 'shop', 'category', 'categories', 'cart', 'checkout', 'order-success'],
  variants: [{ id: 'v1', label: 'Split panel with product cards' }],
  commonFields: [
    { key: 'eyebrow', label: 'Eyebrow', type: 'localized-string', default: 'Fresh every day' },
    { key: 'headline', label: 'Headline', type: 'localized-string', default: 'Everyday essentials, delivered to your door' },
    { key: 'subtext', label: 'Text', type: 'localized-text', default: 'Hand-picked products at fair prices. Order in minutes and pay when it arrives.' },
    { key: 'image', label: 'Picture', type: 'image', help: 'Shown beside the text on large screens. Empty = the first product card’s photo.' },
    { key: 'imageAlt', label: 'Picture description', type: 'string', help: 'For screen readers. Leave empty if the picture is decorative.' },
    { key: 'primaryCta.label', label: 'Main button text', type: 'localized-string', default: 'Shop now' },
    { key: 'primaryCta.url', label: 'Main button link', type: 'string', default: '/shop' },
    { key: 'secondaryCta.label', label: 'Second button text', type: 'localized-string', default: 'Today’s deals' },
    { key: 'secondaryCta.url', label: 'Second button link', type: 'string', default: '/shop?order_by=discount_desc' },
    { key: 'showTrustChips', label: 'Show trust chips', type: 'boolean', default: true },
    {
      key: 'trustChips',
      label: 'Trust chips',
      type: 'array',
      dependency: { field: 'showTrustChips', value: true },
      itemSchema: [
        { key: 'icon', label: 'Icon', type: 'icon', default: 'truck', options: ICON_OPTIONS },
        { key: 'text', label: 'Text', type: 'localized-string' },
      ],
      default: [
        { icon: 'shipping', text: 'Fast delivery' },
        { icon: 'secure', text: 'Cash on delivery' },
        { icon: 'refund', text: 'Easy returns' },
      ],
    },
    { key: 'showCards', label: 'Show product cards', type: 'boolean', default: true },
    {
      key: 'cards',
      label: 'Product cards',
      type: 'array',
      help: 'Up to two. Empty = the two biggest current deals.',
      dependency: { field: 'showCards', value: true },
      itemSchema: [
        { key: 'productId', label: 'Product', type: 'product-select' },
        { key: 'label', label: 'Label', type: 'localized-string', placeholder: 'Bestseller' },
      ],
      default: [],
    },
    {
      key: 'slides',
      label: 'More slides',
      type: 'array',
      help: 'Shown after the headline slide. Pick a product, or write your own text and add a picture.',
      itemSchema: [
        { key: 'productId', label: 'Product', type: 'product-select' },
        { key: 'badge', label: 'Badge', type: 'localized-string', placeholder: 'New' },
        { key: 'title', label: 'Headline', type: 'localized-string', help: 'Empty = the product name.' },
        { key: 'subtitle', label: 'Text', type: 'localized-text' },
        { key: 'image', label: 'Picture', type: 'image', help: 'Empty = the product photo.' },
        { key: 'ctaText', label: 'Button text', type: 'localized-string', placeholder: 'View product' },
        { key: 'ctaLink', label: 'Button link', type: 'string', help: 'Empty = the product page.' },
      ],
      default: [],
    },
    { key: 'autoplay', label: 'Play slides automatically', type: 'boolean', default: true },
    { key: 'autoplaySeconds', label: 'Seconds per slide', type: 'number', default: 6, dependency: { field: 'autoplay', value: true } },
    ...SECTION_STYLE_FIELDS,
  ],
}
